<script setup>
import {onMounted, ref} from 'vue';
import {useDataTable} from '@/utils/datatableHelper';
import {useToast} from "vue-toastification";
import {formatDate, showSuccessMessage} from '@/helper';
import {createConfirmDialog} from 'vuejs-confirm-dialog';
import CreateEditMeetingRecordModal from '@/components/modals/CreateEditMeetingRecordModal.vue';
import InvestmentQuestionnaireService from '@/services/InvestmentQuestionnaireService';
import InvestmentQuestionnaireModal from '@/components/modals/InvestmentQuestionnaireModal.vue';
import ModalDialog from '@/components/modals/ModalDialog.vue';
import MeetingRecordService from '@/services/MeetingRecordService';

import {useI18n} from 'vue-i18n'
import {authData} from '@/services/AuthService';

const {t} = useI18n()

const props = defineProps({
  clientId: Number
});

const toast = useToast();

const isLoading = ref(false);
const datatable = useDataTable(fetchRecords, 'created_at', 10, false);
const rows = ref([]);
const columns = ref([
  {label: t('investmentQuestionnairesIndex.date'), field: 'created_at', sortable: false, filterOptions: {enabled: false}, formatFn: (value) => formatDate(value, 'd.m.Y H:i')},
  {label: t('investmentQuestionnairesIndex.status'), field: 'state', sortable: false},
  {label: t('investmentQuestionnairesIndex.action'), field: 'action', sortable: false},
]);

function fetchRecords(body) {
  body = {...body,...{filter: {client_id: props.clientId}}};
  InvestmentQuestionnaireService.fetchRecords(props.clientId, body).then(response => {
    datatable.totalRecords.value = response.meta.total;
    rows.value = response.data;
  });
}

function editInvestmentQuestionnaire(questionnaire) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(InvestmentQuestionnaireModal, {investmentQuestionnaire: questionnaire, disabled: questionnaire.state !== 'CONCEPT', clientId: props.clientId});
  reveal();
  onConfirm((data) => {
    data.client_id = props.clientId;
    data.creator_id = authData.loggedUser.id;
    InvestmentQuestionnaireService.saveQuestionnaire(props.clientId, data).then(response => {
      showSuccessMessage(response.message);
      datatable.fetchWithValues();
    });
  });
  onCancel(() => {
  });
}

function deleteInvestmentQuestionnaire(questionnaire) {
  const {reveal, onConfirm, onCancel} = createConfirmDialog(ModalDialog, {
    title: t('investmentQuestionnairesIndex.investment_questionnaire_delete'),
    message: t('investmentQuestionnairesIndex.confirm_delete_entry'),
  });
  onConfirm(() => {
    InvestmentQuestionnaireService.deleteRecord(props.clientId, questionnaire.id).then(() => {
      showSuccessMessage(t('investmentQuestionnairesIndex.investment_questionnaire_deleted'));
      datatable.fetchWithValues();
    });
  });
  reveal();
}

onMounted(() => {

})
</script>

<template>
  <div class="page-section">
    <div class="page-section__content">
      <div class="mt-3">
        <vue-good-table
            :columns="columns"
            :rows="rows"
            mode="remote"
            @page-change="datatable.onPageChange"
            @sort-change="datatable.onSortChange"
            @column-filter="datatable.onColumnFilter"
            @per-page-change="datatable.onPerPageChange"
            :totalRows="datatable.totalRecords.value"
            :pagination-options="{
                  enabled: true,
                  rowsPerPageLabel: t('investmentQuestionnairesIndex.entriesPerPage'),
                  nextLabel: t('investmentQuestionnairesIndex.next'),
                  prevLabel: t('investmentQuestionnairesIndex.previous'),
                  ofLabel: 'z',
                }">
          <template #table-row="props">
            <span v-if="props.column.field === 'state'">
            <span v-if="props.row.state === 'DENIED'"> {{ t('investmentQuestionnairesIndex.state_rejected') }} </span>
            <span v-if="props.row.state === 'APPROVED'"> {{ t('investmentQuestionnairesIndex.state_confirmed') }} </span>
            <span v-if="props.row.state === 'WAITING'"> {{ t('investmentQuestionnairesIndex.state_waiting_confirm') }} </span>
            <span v-if="props.row.state === 'TO_REVIEW'"> {{ t('investmentQuestionnairesIndex.state_to_review') }} </span>
            <span v-if="props.row.state === 'CANCELED'"> {{ t('investmentQuestionnairesIndex.state_canceled') }} </span>
            <span v-if="props.row.state === 'ACCEPTED'"> {{ t('investmentQuestionnairesIndex.state_accepted') }} </span>
            <span v-if="props.row.state === 'CONCEPT'"> {{ t('investmentQuestionnairesIndex.state_concept') }} </span>
          </span>
          <span v-if="props.column.field === 'action'">
            <span style="font-weight: bold; float: right">
              <button type="button" class="btn btn-link"><i @click="editInvestmentQuestionnaire(props.row)" class="mdi mdi-eye"/></button>
              <button type="button" class="btn btn-error" @click="deleteInvestmentQuestionnaire(props.row)"><i class="mdi mdi-trash-can-outline"/></button>
            </span>
          </span>
          </template>
          <template v-slot:emptystate v-if="!isLoading">
            <div class="text-center">
              {{ t('investmentQuestionnairesIndex.no_records_found') }}
            </div>
          </template>
          <template v-slot:emptystate v-if="isLoading">
            <div class="text-center">
              {{ t('investmentQuestionnairesIndex.data_loading') }}
            </div>
          </template>
        </vue-good-table>
      </div>
    </div>

  </div>
</template>

<style scoped>

</style>
